import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import CountDown from './Components/CountDown';
import Button from 'src/Components/Button_V2';
import { usePrivateSale } from './PrivateSaleProvider';
import IntervalTicker from 'src/utils/intervalTicker';
import { ProjectStatus } from 'src/utils/types';
import { chainIconMap } from './config';
import { useUserWalletContext } from '@profile/src/Profile_V2/UserWalletProvider';
import { formatCurrency } from '@wolffun/system-currency';
import { calculateReceivedToken } from './utils';

const ProjectPanel = ({
  className,
  detail,
  userProject
}) => {
  const {
    showAgreement,
    showOrderDetail,
    showRefundedDetail
  } = usePrivateSale();
  const [remainingTime, setRemainingTime] = React.useState(0);
  const [isEnded, setIsEnded] = React.useState(false);
  const {
    projectStatus,
    chainName,
    tokenSale: {
      saleEndTime,
      saleStartTime
    },
    totalSale,
    totalSold,
    shortDesc,
    buyingProcessPercent,
    maxAllocation,
    pricePerToken,
    totalBuyer,
    id
  } = detail;
  const chainIcon = chainIconMap[chainName];
  const {
    checkWalletError
  } = useUserWalletContext();
  React.useEffect(() => {
    let endTime = 0;

    if (saleEndTime !== undefined && projectStatus === ProjectStatus.LIVE) {
      endTime = saleEndTime;
    }

    if (saleStartTime !== undefined && projectStatus === ProjectStatus.UPCOMING) {
      endTime = saleStartTime;
    }

    if (!endTime) {
      return () => {};
    }

    const intervalTicket = new IntervalTicker(Date.now(), new Date(endTime).getTime(), timestamp => {
      setRemainingTime(timestamp);
    }, () => {
      setIsEnded(true);
    });
    intervalTicket.start();
    return () => {
      intervalTicket.clear();
    };
  }, [projectStatus, saleEndTime, saleStartTime]);
  const renderButton = React.useCallback(() => {
    const onPress = () => {
      if (checkWalletError()) {
        return;
      }

      showAgreement(id);
    };

    if (projectStatus === ProjectStatus.ENDED || projectStatus === ProjectStatus.LIVE && isEnded) {
      return /*#__PURE__*/React.createElement("div", {
        className: styles.buttonGroup
      }, /*#__PURE__*/React.createElement(Button, {
        disabled: true,
        className: styles.btn,
        title: "Ended",
        type: "warning"
      }));
    }

    switch (projectStatus) {
      case ProjectStatus.LIVE:
        return /*#__PURE__*/React.createElement("div", {
          className: styles.buttonGroup
        }, /*#__PURE__*/React.createElement("div", {
          className: styles.infoText
        }, /*#__PURE__*/React.createElement("span", null, `You only have 1 chance to purchase each project. Seize the
                  chance! Max investment amount is ${formatCurrency(maxAllocation, {
          withCurrency: false,
          trimWholeFraction: true
        })}$`)), /*#__PURE__*/React.createElement(Button, {
          onClick: onPress,
          className: styles.btn,
          title: "Place order",
          type: "warning"
        }));

      case ProjectStatus.UPCOMING:
        return /*#__PURE__*/React.createElement("div", {
          className: styles.buttonGroup
        }, /*#__PURE__*/React.createElement("div", {
          className: styles.infoText
        }, /*#__PURE__*/React.createElement("span", null, `You only have 1 chance to purchase each project. Seize the
                  chance! Max investment amount is ${formatCurrency(maxAllocation, {
          withCurrency: false,
          trimWholeFraction: true
        })}$`)), /*#__PURE__*/React.createElement(Button, {
          onClick: onPress,
          disabled: true,
          className: classNames(styles.btn, styles.semiDisabled),
          title: "Place order",
          type: "warning"
        }));

      default:
        return /*#__PURE__*/React.createElement("div", {
          className: styles.buttonGroup
        }, /*#__PURE__*/React.createElement(Button, {
          disabled: true,
          className: styles.btn,
          title: "Ended",
          type: "warning"
        }));
    }
  }, [projectStatus, isEnded, checkWalletError, showAgreement, id, maxAllocation]);

  const renderYourOrder = () => /*#__PURE__*/React.createElement("div", {
    className: styles.yourOrder
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.titleContainer
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Your order")), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.name
  }, "Tickets used"), /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  }), userProject && /*#__PURE__*/React.createElement("span", {
    className: styles.orderValue
  }, formatCurrency(userProject.pTicketAmount, {
    trimWholeFraction: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.name
  }, "Investment amount"), /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  }), userProject && /*#__PURE__*/React.createElement("span", {
    className: styles.orderValue
  }, formatCurrency(userProject.busdAmount, {
    withCurrency: true,
    trimWholeFraction: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.row, styles.background)
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.name
  }, "Tokens secured"), /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  }), userProject && /*#__PURE__*/React.createElement("span", {
    className: styles.orderValue
  }, `${calculateReceivedToken(userProject.busdAmount, userProject.pricePerToken)} ${detail.symbol}`)), /*#__PURE__*/React.createElement(Button, {
    onClick: () => projectStatus === ProjectStatus.REFUNDED ? userProject && showRefundedDetail(userProject.projectId, detail.symbol ? detail.symbol : 'TBA') : userProject && showOrderDetail(userProject.projectId, detail.symbol ? detail.symbol : 'TBA'),
    className: styles.btn,
    title: "View more",
    type: "primary"
  }));

  const renderBottom = () => {
    if (userProject) {
      return renderYourOrder();
    }

    return renderButton();
  };

  return /*#__PURE__*/React.createElement("div", {
    className: classNames(className, styles.container)
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.info
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Private Sale Pool"), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.price
  }, `$${formatCurrency(totalSale, {
    trimWholeFraction: true,
    withCurrency: false
  })}`), /*#__PURE__*/React.createElement("img", {
    className: styles.icon,
    title: chainName,
    src: chainIcon
  })), /*#__PURE__*/React.createElement("span", {
    className: styles.desc
  }, shortDesc), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Personal allocation"), /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.value
  }, `$${formatCurrency(maxAllocation, {
    withCurrency: false,
    trimWholeFraction: true
  })} Max`)), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Price per token"), /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.value
  }, parseInt(pricePerToken.value) > 0 ? `$${formatCurrency(pricePerToken, {
    trimWholeFraction: true,
    withCurrency: false
  })}` : 'TBA')), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.title
  }, "Sold"), /*#__PURE__*/React.createElement("div", {
    className: styles.dot
  }), /*#__PURE__*/React.createElement("span", {
    className: styles.value
  }, `$${formatCurrency(totalSold, {
    trimWholeFraction: true,
    withCurrency: false
  })}/$${formatCurrency(totalSale, {
    trimWholeFraction: true,
    withCurrency: false
  })}`))), /*#__PURE__*/React.createElement(CountDown, {
    contributors: totalBuyer,
    percent: buyingProcessPercent,
    remainingTime: projectStatus === ProjectStatus.ENDED ? 0 : remainingTime,
    projectStatus: projectStatus
  }), renderBottom());
};

export default ProjectPanel;